import { useRef, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { IMMERSIVE } from "@/content/immersive-media";
import InlineText from "@/components/InlineText";
import InlineMediaButton from "@/components/InlineMediaButton";
import InlineGalleryEditor, { resolveSrc } from "@/components/InlineGalleryEditor";
import { useContent } from "@/lib/ContentProvider";

/**
 * Page immersive d'une expérience (bain de fleurs, petit déjeuner, décors).
 * Contient : hero (vidéo plein écran ou image fixe) + galerie + retour discret.
 */
export default function ImmersiveExperience() {
  const { slug } = useParams();
  const data = IMMERSIVE[slug];
  const { mediaUrl, content, saveText } = useContent();
  const cms = content?.immersive?.[slug] || {};
  const val = (k, def) => (cms[k] !== undefined && cms[k] !== null && cms[k] !== "" ? cms[k] : def);

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [audioOn, setAudioOn] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    setVideoReady(false);
    setPlaying(true);
    setAudioOn(false);
  }, [slug]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [playing]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = 0.35;
    if (audioOn) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, [audioOn]);

  if (!data) {
    return (
      <div data-testid="immersive-not-found" className="min-h-screen bg-refuge-bg flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-heading text-2xl text-refuge-text mb-4">Expérience introuvable</p>
          <Link to="/experiences" className="font-body text-sm text-refuge-ruby underline">
            Retour aux expériences
          </Link>
        </div>
      </div>
    );
  }

  // URL du poster passée par le CMS (remplaçable via admin, avec cache-buster)
  // Les 3 slots hero (video/poster/audio) peuvent être overridés depuis
  // le CMS immersive.{slug}.heroVideoFile / heroPosterFile / heroAudioFile.
  // Un flag `heroNoVideo` permet de forcer un poster fixe même si le fichier
  // statique de fallback définit une vidéo (ex: bain de fleurs historique).
  const cmsHeroVideo = cms.heroVideoFile;
  const cmsHeroPoster = cms.heroPosterFile;
  const cmsHeroAudio = cms.heroAudioFile;

  const posterUrl = cmsHeroPoster
    ? resolveSrc(cmsHeroPoster)
    : (mediaUrl ? mediaUrl(data.hero.poster) : data.hero.poster);
  const videoUrl = cms.heroNoVideo
    ? null
    : (cmsHeroVideo ? resolveSrc(cmsHeroVideo) : data.hero.video);
  const audioUrl = cmsHeroAudio ? resolveSrc(cmsHeroAudio) : data.hero.audio;
  const hasVideo = !!videoUrl;
  const hasAudio = !!audioUrl;

  // Galerie : source = CMS override ou fallback statique
  const cmsGallery = Array.isArray(cms.gallery) ? cms.gallery : null;
  const gallery = cmsGallery || data.gallery || [];

  return (
    <div data-testid={`immersive-${data.slug}`} className="bg-refuge-bg min-h-screen">
      {/* Retour flottant en haut à gauche */}
      <Link
        to="/experiences"
        data-testid="immersive-back-top"
        className="fixed top-6 left-6 md:top-8 md:left-8 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-[11px] tracking-[0.18em] uppercase font-body hover:bg-white/25 transition-all duration-500"
        style={{ textShadow: "0 1px 4px rgba(0,0,0,0.35)" }}
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} />
        Expériences
      </Link>

      {/* ===== HERO ===== */}
      <section
        data-testid="immersive-hero"
        className="relative w-full h-[85vh] md:h-[92vh] min-h-[560px] overflow-hidden bg-refuge-text"
      >
        {/* Poster : image de fond, animée en Ken-Burns si pas de vidéo */}
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-1000"
          style={{
            backgroundImage: `url(${posterUrl})`,
            opacity: hasVideo && videoReady ? 0 : 1,
            animation: hasVideo ? "none" : "cr-kenburns 24s ease-in-out infinite alternate",
          }}
        />
        {hasVideo && (
          <video
            key={`${data.slug}-${videoUrl}`}
            ref={videoRef}
            playsInline
            autoPlay
            loop
            muted
            preload="metadata"
            poster={posterUrl}
            onLoadedData={() => setVideoReady(true)}
            onCanPlay={() => setVideoReady(true)}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: videoReady ? 1 : 0 }}
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/quicktime" />
          </video>
        )}
        {hasAudio && (
          <audio ref={audioRef} src={audioUrl} loop preload="auto" />
        )}

        {/* Contrôles admin — remplacement des 3 slots médias du hero */}
        <InlineMediaButton
          label="Vidéo hero"
          position="tl"
          accept="video/*"
          currentValue={cmsHeroVideo || (data.hero.video ? data.hero.video.split("/").pop() : "(aucune)")}
          onPick={(filename) => saveText(`immersive.${slug}.heroVideoFile`, filename)}
          allowClear
          onClear={() => saveText(`immersive.${slug}.heroNoVideo`, !cms.heroNoVideo)}
          dataTestid={`hero-video-edit-${slug}`}
        />
        <InlineMediaButton
          label="Poster hero"
          position="tc"
          accept="image/*"
          currentValue={cmsHeroPoster || (data.hero.poster ? data.hero.poster.split("/").pop() : "")}
          onPick={(filename) => saveText(`immersive.${slug}.heroPosterFile`, filename)}
          dataTestid={`hero-poster-edit-${slug}`}
        />
        <InlineMediaButton
          label="Audio hero"
          position="tr"
          accept="audio/*"
          currentValue={cmsHeroAudio || (data.hero.audio ? data.hero.audio.split("/").pop() : "(aucun)")}
          onPick={(filename) => saveText(`immersive.${slug}.heroAudioFile`, filename)}
          allowClear
          onClear={() => saveText(`immersive.${slug}.heroAudioFile`, "")}
          dataTestid={`hero-audio-edit-${slug}`}
        />
        {cms.heroNoVideo && (
          <div className="absolute top-36 left-3 z-30 px-2 py-1 rounded-full bg-black/60 text-white text-[10px] font-mono backdrop-blur-sm">
            Vidéo hero désactivée · seul le poster est affiché
          </div>
        )}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.55) 100%)" }}
        />

        {/* Titre au centre */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <InlineText
            as="p"
            path={`immersive.${slug}.overline`}
            value={val("overline", data.overline)}
            onSave={(v) => saveText(`immersive.${slug}.overline`, v)}
            data-testid="immersive-overline"
            className="font-body text-white/70 uppercase mb-5 md:mb-7 cr-reveal"
            style={{ fontSize: "10px", letterSpacing: "0.35em", animationDelay: "0.15s" }}
          />
          <h1
            data-testid="immersive-title"
            className="font-heading font-light text-white text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.05] cr-reveal"
            style={{ textShadow: "0 2px 22px rgba(0,0,0,0.4)", animationDelay: "0.35s" }}
          >
            <InlineText
              path={`immersive.${slug}.title`}
              value={val("title", data.title)}
              onSave={(v) => saveText(`immersive.${slug}.title`, v)}
              multiline
            />
          </h1>
          {(data.subtitle || cms.subtitle) && (
            <InlineText
              as="p"
              multiline
              path={`immersive.${slug}.subtitle`}
              value={val("subtitle", data.subtitle)}
              onSave={(v) => saveText(`immersive.${slug}.subtitle`, v)}
              className="font-heading italic font-light text-white/85 text-base md:text-xl mt-6 md:mt-8 max-w-2xl leading-relaxed cr-reveal"
              style={{ textShadow: "0 2px 14px rgba(0,0,0,0.4)", animationDelay: "0.55s" }}
            />
          )}
        </div>

        {/* Contrôles — n'apparaissent que si on a une vidéo ou un audio */}
        {(hasVideo || hasAudio) && (
          <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 z-10 flex items-center gap-3">
            {hasAudio && (
              <button
                type="button"
                data-testid="immersive-audio-btn"
                onClick={() => setAudioOn(!audioOn)}
                className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/25 hover:bg-white/20 transition-all"
                aria-label={audioOn ? "Couper l'ambiance sonore" : "Activer l'ambiance sonore"}
              >
                {audioOn ? (
                  <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={1.2} />
                ) : (
                  <VolumeX className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={1.2} />
                )}
              </button>
            )}
            {hasVideo && (
              <button
                type="button"
                data-testid="immersive-play-btn"
                onClick={() => setPlaying(!playing)}
                className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/25 hover:bg-white/20 transition-all"
                aria-label={playing ? "Mettre en pause" : "Lancer la vidéo"}
              >
                {playing ? (
                  <Pause className="w-4 h-4 md:w-5 md:h-5 text-white fill-white" strokeWidth={1.2} />
                ) : (
                  <Play className="w-4 h-4 md:w-5 md:h-5 text-white fill-white" strokeWidth={1.2} />
                )}
              </button>
            )}
          </div>
        )}
      </section>

      {/* ===== GALERIE IMMERSIVE ===== */}
      <section className="bg-refuge-bg py-20 md:py-28" data-testid="immersive-gallery">
        <div className="max-w-6xl mx-auto px-6">
          <InlineText
            as="p"
            path={`immersive.${slug}.galleryEyebrow`}
            value={val("galleryEyebrow", "L'atmosphère")}
            onSave={(v) => saveText(`immersive.${slug}.galleryEyebrow`, v)}
            className="font-body text-xs tracking-[0.24em] uppercase text-refuge-ruby text-center mb-3"
          />
          <h2 className="font-heading text-3xl md:text-4xl font-light text-refuge-text text-center mb-14 md:mb-16 leading-tight">
            <InlineText
              path={`immersive.${slug}.galleryTitle`}
              value={val("galleryTitle", "En images")}
              onSave={(v) => saveText(`immersive.${slug}.galleryTitle`, v)}
            />
          </h2>

          <InlineGalleryEditor
            items={gallery}
            slug={slug}
            onChange={(next) => saveText(`immersive.${slug}.gallery`, next)}
          >
            {gallery.length === 0 ? (
              <p className="text-center py-14 text-refuge-soft font-body italic text-sm">
                Aucune photo dans la galerie pour le moment.
              </p>
            ) : gallery.length === 1 ? (
              <div className="max-w-3xl mx-auto">
                <figure
                  className="overflow-hidden rounded-2xl border border-refuge-border/60"
                  style={{ boxShadow: "0 20px 50px -28px rgba(45,40,37,0.4)" }}
                >
                  <img
                    src={gallery[0].src?.startsWith("http") || gallery[0].src?.startsWith("/") ? (mediaUrl ? mediaUrl(gallery[0].src) : gallery[0].src) : resolveSrc(gallery[0].src)}
                    alt={gallery[0].alt}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1400ms] hover:scale-[1.03]"
                  />
                </figure>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5" data-testid="immersive-bento">
                {gallery.map((img, i) => {
                  const layout = i === 0 ? "md:col-span-6" : "md:col-span-3";
                  const ratio = i === 0 ? "aspect-[16/9] md:aspect-[21/9]" : "aspect-[4/3]";
                  const src = img.src?.startsWith("http") || img.src?.startsWith("/")
                    ? (mediaUrl ? mediaUrl(img.src) : img.src)
                    : resolveSrc(img.src);
                  return (
                    <figure
                      key={i}
                      className={`${layout} overflow-hidden rounded-2xl border border-refuge-border/60 cr-reveal`}
                      style={{
                        boxShadow: "0 16px 40px -26px rgba(45,40,37,0.35)",
                        animationDelay: `${0.12 * i}s`,
                      }}
                    >
                      <img
                        src={src}
                        alt={img.alt}
                        loading="lazy"
                        className={`w-full ${ratio} object-cover transition-transform duration-[1400ms] hover:scale-[1.04]`}
                      />
                    </figure>
                  );
                })}
              </div>
            )}
          </InlineGalleryEditor>
        </div>
      </section>

      {/* ===== RETOUR ===== */}
      <section className="bg-refuge-bg pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Link
            to="/experiences"
            data-testid="immersive-back-cta"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-refuge-ruby text-white font-body text-sm tracking-wide hover:bg-refuge-ruby/90 transition-all"
          >
            <ArrowLeft size={16} strokeWidth={1.3} />{" "}
            <InlineText
              path={`immersive.${slug}.backLabel`}
              value={val("backLabel", "Retour aux expériences")}
              onSave={(v) => saveText(`immersive.${slug}.backLabel`, v)}
            />
          </Link>
        </div>
      </section>
    </div>
  );
}
